import axios from 'axios';
import { message } from 'antd';
import { useAppLocation, useNavigation } from '@/common/hooks/navigation';
import { captureError } from '../utils/sentry';

// Global loading state management
let activeRequestsCount = 0;
const loadingStateCallbacks = [];

export const addLoadingStateCallback = (callback) => {
  loadingStateCallbacks.push(callback);
};

export const removeLoadingStateCallback = (callback) => {
  const index = loadingStateCallbacks.indexOf(callback);
  if (index > -1) {
    loadingStateCallbacks.splice(index, 1);
  }
};

const updateLoadingState = (isLoading) => {
  loadingStateCallbacks.forEach(callback => callback(isLoading));
};
message.config({
  top: 94,
  duration: 3,
  maxCount: 1,
});

// 响应时间
axios.defaults.timeout = 50 * 1000;

// 配置cookie
// axios.defaults.withCredentials = true

// 配置请求头
axios.defaults.headers.post['Content-Type'] =
  'application/x-www-form-urlencoded;charset=UTF-8';

axios.defaults.retry = 1;
axios.defaults.retryDelay = 1000;

// POST传参序列化(添加请求拦截器)
axios.interceptors.request.use(
  config => {
    // Track request start
    activeRequestsCount++;
    updateLoadingState(true);

    const _token = window.localStorage.getItem('token') || null;

    if (_token) {
      if (!config.headers) {
        config.headers = {};
      }
      config.headers['Authorization'] = _token;
    }

    // Debug payment requests
    if (config.url && config.url.includes('/payment/stripe/create_payment')) {
      console.log('===== HTTP REQUEST DEBUG =====');
      console.log('URL:', config.url);
      console.log('Method:', config.method);
      console.log('Params:', config.params);
      console.log('Data:', config.data);
      console.log('Full config:', config);
    }

    return config;
  },
  err => {
    // Track request end on error
    activeRequestsCount--;
    if (activeRequestsCount <= 0) {
      activeRequestsCount = 0;
      updateLoadingState(false);
    }
    return Promise.reject(err);
  },
);

// 返回状态判断(添加响应拦截器)
axios.interceptors.response.use(
  res => {
    // Track request end
    activeRequestsCount--;
    if (activeRequestsCount <= 0) {
      activeRequestsCount = 0;
      updateLoadingState(false);
    }

    const { data, status, config } = res;
    const successCodes = [200, 201, 204];
    if (!successCodes.includes(status)) {
      if (!config.hideMessageModal) {
        message.error(res.data.message);
      }

      return Promise.reject(res);
    } else {
      if (!data || res.status === 204) return Promise.resolve(res);
      const { code } = data;

      // Add code 2010 (verification code sent) to accepted success codes
      const apiSuccessCodes = [...successCodes, 2010];

      // Special handling for Slack notifications - if success field is true, treat as success
      if (
        config.url &&
        config.url.includes('/slack-notification') &&
        data.success === true
      ) {
        return Promise.resolve(res);
      }

      if (!apiSuccessCodes.includes(code)) {
        if (!config.hideMessageModal) {
          message.error(data.message);
        }
        return Promise.reject(res);
      }
      return Promise.resolve(res);
    }
  },
  error => {
    // Track request end on error
    activeRequestsCount--;
    if (activeRequestsCount <= 0) {
      activeRequestsCount = 0;
      updateLoadingState(false);
    }

    if (error?.response?.status === 204) {
      return Promise.resolve(error.response);
    }
    if (error?.response?.status === 401) {
      message.error(error?.response.data?.message, 1);
      window.localStorage.clear();
      const from = location.pathname;
      navigation.replace(`/login?from=${encodeURIComponent(from)}`);
      return Promise.resolve(error.response);
    }

    // Capture API errors with Sentry (but not 401s as they're expected)
    const shouldCaptureError =
      error?.response?.status !== 401 &&
      error?.response?.status !== 204 &&
      error?.response?.status >= 400;

    if (shouldCaptureError) {
      captureError(error, {
        api_endpoint: error?.config?.url,
        api_method: error?.config?.method,
        status_code: error?.response?.status,
        response_data: error?.response?.data,
        request_data: error?.config?.data,
      });
    }

    return Promise.reject(error);
  },
);

function removeToken() {
  window.localStorage.clear();
  window.sessionStorage.clear();
  // 通知 91 trial 退出
  if (window?.qiankunInstance) {
    window?.qiankunInstance?.qiankunInstanceActions?.setGlobalState({
      isAuthError: true,
    });
  }
  window.location.reload();
}

// 发送请求
export function post(url, params, config) {
  return new Promise((resolve, reject) => {
    axios
      .post(url, params, config)
      .then(
        res => {
          resolve(res);
        },
        err => {
          reject(err?.data);
        },
      )
      .catch(err => {
        reject(err?.data);
      });
  });
}
export function get(url, params, config) {
  return new Promise((resolve, reject) => {
    axios
      .get(url, {
        params: params,
      })
      .then(res => {
        resolve(res.data, config);
      })
      .catch(err => {
        reject(err);
      });
  });
}

export function Put(url, params, config) {
  return new Promise((resolve, reject) => {
    axios
      .put(url, { ...params }, config)
      .then(res => {
        resolve(res.data);
      })
      .catch(err => {
        reject(err);
      });
  });
}

export function Delete(url, params, config) {
  return new Promise((resolve, reject) => {
    axios
      .delete(url, { params: params }, config)
      .then(res => {
        resolve(res.data);
      })
      .catch(err => {
        reject(err);
      });
  });
}

// 发送请求  resultful风格接口
export function resultfulPost(url, params, params2) {
  return new Promise((resolve, reject) => {
    axios
      .post(`${url}?${qs.stringify(params2)}`, params)
      .then(
        res => {
          resolve(res.data);
        },
        err => {
          reject(err.data);
        },
      )
      .catch(err => {
        reject(err);
      });
  });
}
export function RePost(url, _params, _params2) {
  const params = _params ? `/${_params.join('/')}` : '';
  return new Promise((resolve, reject) => {
    axios
      .post(url + params, _params2)
      .then(res => {
        resolve(res.data);
      })
      .catch(err => {
        reject(err);
      });
  });
}
export function resultfulGet(url, _params, _params2) {
  const params = _params ? `/${_params.join('/')}` : '';
  return new Promise((resolve, reject) => {
    axios
      .get(url + params, {
        params: _params2,
      })
      .then(res => {
        resolve(res.data);
      })
      .catch(err => {
        reject(err);
      });
  });
}
export function resultfulPut(url, _params, _params2) {
  const params = _params ? `/${_params.join('/')}` : '';
  return new Promise((resolve, reject) => {
    axios
      .put(url + params, _params2)
      .then(res => {
        resolve(res.data);
      })
      .catch(err => {
        reject(err);
      });
  });
}
export function resultfulDelete(url, id) {
  return new Promise((resolve, reject) => {
    axios
      .delete(url + `/${id}`)
      .then(res => {
        resolve(res.data);
      })
      .catch(err => {
        reject(err);
      });
  });
}
